import type {
  FamilyCheckoutStartResponse,
  FamilyPortalSession,
  FamilyLoginRequestResponse,
  FamilyPurchaseOffer,
} from "@shared/types/familyPortal";

/**
 * The relatives portal's API client (PORTAL-2).
 *
 * Same-origin by design: `family.nenda.com/api/...` is forwarded to the API by
 * the edge-router Worker (PORTAL-1), so the `foyer_family` cookie is a
 * first-party cookie on the only host that ever sends it. That is what lets it be
 * `httpOnly` + `sameSite: lax` and still work - no CORS, no token in JS, nothing
 * for a script on another origin to read.
 *
 * `credentials: "same-origin"` is stated on every call rather than relied on as a
 * default, matching src/guest/api.ts.
 */

/** Thrown for any non-2xx. `status` is what the UI branches on (401 = signed out). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string
  ) {
    super(code);
    this.name = "ApiError";
  }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, await errorCode(res));
  return (await res.json()) as T;
}

async function errorCode(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? `http_${res.status}`;
  } catch {
    return `http_${res.status}`;
  }
}

export const familyApi = {
  /**
   * Ask for a magic link. ALWAYS resolves for a well-formed request, whether or
   * not the address is known - the server refuses to say (see
   * FamilyLoginRequestResponse), so the UI must not pretend to know either. The
   * one failure the UI can see is 429, our own send budget.
   */
  requestLogin(email: string): Promise<FamilyLoginRequestResponse> {
    return post<FamilyLoginRequestResponse>("/api/family/login/request", {
      email,
    });
  },

  /** Trade the single-use link for a durable session. 401 = link no longer valid. */
  consumeLogin(token: string): Promise<FamilyPortalSession> {
    return post<FamilyPortalSession>(
      `/api/family/login/consume/${encodeURIComponent(token)}`
    );
  },

  /** The signed-in payload, or an ApiError(401) when there is no live session. */
  async session(): Promise<FamilyPortalSession> {
    const res = await fetch("/api/family/session", {
      credentials: "same-origin",
    });
    if (!res.ok) throw new ApiError(res.status, await errorCode(res));
    return (await res.json()) as FamilyPortalSession;
  },

  /** Sign this browser out. Best-effort: the cookie is cleared server-side. */
  logout(): Promise<{ ok: true }> {
    return post<{ ok: true }>("/api/family/logout");
  },

  /**
   * Can this relative buy calling, and for how much (PORTAL-3)?
   *
   * One server-computed verdict rather than three booleans the client would have
   * to combine - see FamilyPurchaseState. 401 = signed out, same as `session`.
   */
  async purchaseOffer(): Promise<FamilyPurchaseOffer> {
    const res = await fetch("/api/family/purchase", {
      credentials: "same-origin",
    });
    if (!res.ok) throw new ApiError(res.status, await errorCode(res));
    return (await res.json()) as FamilyPurchaseOffer;
  },

  /**
   * Open a hosted checkout and get the URL to send the browser to.
   *
   * Creates NO entitlement, so it is safe to retry. The refusals worth branching
   * on are 409 (`already_active` / `calling_disabled` / `no_active_stay` - the
   * world moved since the offer was rendered; re-read it) and 503
   * (`unavailable` - we cannot take money yet).
   */
  startCheckout(): Promise<FamilyCheckoutStartResponse> {
    return post<FamilyCheckoutStartResponse>("/api/family/purchase/checkout");
  },
};
